import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle2, Lock, Sparkles, Rocket } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import AppShell from "@/components/nav/AppShell";
import HealthCheckCard from "@/components/HealthCheckCard";
import UsageWidget from "@/components/UsageWidget";
import WeeklyAnalysisCard from "@/components/WeeklyAnalysisCard";
import UnverifiedBanner from "@/components/auth/UnverifiedBanner";

const PLAN_INTERVAL = { starter: 7, growth: 3, scale: 2 };

// PR: delayed-paywall. Decides where the "Continue setup" button leads —
// the user's last unfinished onboarding step, or step 1 if they haven't
// started yet. Mirrors the backend's onboarding_step enum.
const ONBOARDING_RESUME_PATH = {
    "1": "/onboarding/step-1",
    "2": "/onboarding/step-2",
    "3": "/onboarding/step-3",
    "4": "/onboarding/step-3", // legacy — step-4 was merged into step-3
};

const Dashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Onboarding-incomplete users get the empty state hero — they can still
    // access /settings (delete account, profile) via the sidebar but won't
    // see any data widgets that depend on a generated strategy.
    if (user && user.onboarding_step !== "done") {
        return <OnboardingHero user={user} navigate={navigate} />;
    }

    return <DashboardContent user={user} navigate={navigate} />;
};

// Empty-state hero for users who signed up but haven't finished onboarding.
// Big friendly CTA + reassuring copy. NOT a paywall — the paywall lives on
// Step 3 itself. Free users get to fill out their profile and connect their
// Google Ads before they're asked to pay.
const OnboardingHero = ({ user, navigate }) => {
    const step = user?.onboarding_step;
    const resumePath = ONBOARDING_RESUME_PATH[step] || "/onboarding/step-1";
    const hasStarted = !!step && step !== "1";
    const ctaLabel = hasStarted ? "Продовжити налаштування" : "🚀 Почати налаштування";
    const headline = hasStarted
        ? `З поверненням, ${user?.name?.split(" ")[0] || ""}!`
        : `Привіт, ${user?.name?.split(" ")[0] || ""}!`;
    const subline = hasStarted
        ? "Ви на крок ближче до запущених кампаній — продовжимо?"
        : "Розкажіть нам про свій бізнес і ми за 20 хвилин зробимо стратегію реклами під ключ.";

    return (
        <AppShell testid="dashboard-onboarding-hero">
            <UnverifiedBanner />

            <div className="mt-6 max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-mono text-secondary-c">
                    <Rocket size={12} className="text-primary-c" />
                    {hasStarted ? `Крок ${step} з 3` : "Готовий старт"}
                </div>
                <h1
                    className="mt-5 font-display font-extrabold text-text-primary"
                    style={{ fontSize: "clamp(32px, 4.5vw, 48px)", letterSpacing: "-0.02em" }}
                    data-testid="dashboard-hero-heading"
                >
                    {headline}
                </h1>
                <p className="mt-4 text-lg text-secondary-c">{subline}</p>

                <button
                    onClick={() => navigate(resumePath)}
                    className="mt-8 btn-primary text-base px-7 py-3"
                    data-testid="dashboard-start-onboarding"
                >
                    {ctaLabel}
                </button>

                <p className="mt-4 text-xs text-faint-c font-mono">
                    Безкоштовно до моменту генерації стратегії • без картки на старті
                </p>
            </div>

            <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="dashboard-feature-preview">
                {[
                    { t: "1. Розкажіть про бізнес", d: "Кілька питань про нішу, аудиторію та цілі. Без води." },
                    { t: "2. Підключіть Google Ads", d: "Один клік. Ми лише читаємо статистику — нічого не міняємо без вас." },
                    { t: "3. Отримайте стратегію", d: "AI готує кампанії, ключові слова та оголошення. Тут потрібна підписка." },
                ].map((c, i) => (
                    <div key={i} className="glass rounded-xl p-5">
                        <h3 className="font-display font-semibold text-base text-text-primary">{c.t}</h3>
                        <p className="mt-2 text-sm text-secondary-c">{c.d}</p>
                    </div>
                ))}
            </section>
        </AppShell>
    );
};

// Full dashboard — only rendered for onboarded users. Pulled forward from
// the pre-paywall version (restored from commit 836e2e0; the file was
// accidentally overwritten with the Pricing component in c96d111).
const DashboardContent = ({ user, navigate }) => {
    const [strategy, setStrategy] = useState(null);
    const [business, setBusiness] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [s, b] = await Promise.all([api.get("/strategy"), api.get("/business")]);
                setStrategy(s.data || null);
                setBusiness(b.data || null);
            } catch {}
        })();
    }, []);

    let nextAnalysisText = "—";
    let analysisReady = false;
    if (strategy?.launched_at && user?.plan) {
        const interval = PLAN_INTERVAL[user.plan] || 7;
        const next = new Date(new Date(strategy.launched_at).getTime() + interval * 86400000);
        const diff = next - new Date();
        analysisReady = diff <= 0;
        nextAnalysisText = analysisReady
            ? "Готово до аналізу"
            : (() => {
                const days = Math.ceil(diff / 86400000);
                return `Перший аналіз через ${days} ${days === 1 ? "день" : "днів"}`;
            })();
    }

    return (
        <AppShell testid="dashboard-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display font-extrabold text-text-primary" style={{ fontSize: "clamp(32px, 4.5vw, 48px)", letterSpacing: "-0.02em" }}>
                        Вітаємо, {user?.name?.split(" ")[0] || "власник"}!
                    </h1>
                    <p className="mt-3 text-secondary-c">Ваш AI-маркетолог працює у фоні. Ось ваш прогрес.</p>
                </div>
                {business?.name && (
                    <div className="text-sm text-text-primary font-medium glass rounded-lg px-3 py-2" data-testid="db-business-name">
                        {business.name}
                    </div>
                )}
            </div>

            <div className="mt-5">
                <UnverifiedBanner />
            </div>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="db-quick-actions">
                <button
                    onClick={() => navigate("/strategy")}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-primary/40 bg-primary/10 text-primary-c hover:bg-primary/20 transition"
                    data-testid="db-open-strategy-btn"
                >
                    <Sparkles size={14} /> Стратегія та кампанії
                </button>
            </div>

            <section className="mt-8" data-testid="db-usage-section">
                <UsageWidget />
            </section>

            <section className="mt-8 glass rounded-2xl p-7" data-testid="db-progress-card">
                <h2 className="font-display font-bold text-xl text-text-primary">Прогрес запуску</h2>
                <ul className="mt-6 space-y-4">
                    <ProgressStep done label="Крок 1: Розповіли про бізнес" />
                    <ProgressStep done label="Крок 2: Отримали структуру кампанії" />
                    <ProgressStep done label={`Крок 3: Запустили рекламу ${strategy?.launched_at ? `(${new Date(strategy.launched_at).toLocaleDateString("uk-UA")})` : ""}`} />
                    <ProgressStep
                        pending
                        label="Крок 4: Чекаємо результати"
                        sub={
                            <div className="mt-2">
                                <div className="font-mono text-2xl text-primary-c" data-testid="db-next-analysis">{nextAnalysisText}</div>
                            </div>
                        }
                    />
                    <ProgressStep locked={!analysisReady} label="Крок 5: Завантажте звіт → отримайте рекомендації" />
                </ul>
            </section>

            {strategy?.launched && (
                <section className="mt-8" data-testid="db-health-section">
                    <HealthCheckCard />
                </section>
            )}

            {strategy?.launched && (
                <section className="mt-8" data-testid="db-weekly-analysis-section">
                    <WeeklyAnalysisCard
                        nextAnalysisAt={strategy?.next_analysis_date}
                        lastAnalysisAt={strategy?.last_analysis_at}
                    />
                </section>
            )}

            <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                    { t: "Перевірте статус кампанії", d: "В Google Ads має бути 'Активна' або 'Прийнятна'.", testid: "tip-status" },
                    { t: "Встановіть конверсії", d: "Якщо є сайт — додайте Google Tag для точніших звітів.", testid: "tip-conv" },
                    { t: "Налаштуйте Google Business", d: "Безкоштовно. Підсилить локальну рекламу і Maps.", testid: "tip-gbp" },
                ].map((c) => (
                    <div key={c.testid} data-testid={c.testid} className="glass rounded-xl p-5">
                        <h3 className="font-display font-semibold text-base text-text-primary">{c.t}</h3>
                        <p className="mt-2 text-sm text-secondary-c">{c.d}</p>
                    </div>
                ))}
            </section>
        </AppShell>
    );
};

const ProgressStep = ({ done, pending, locked, label, sub }) => (
    <li className="flex items-start gap-4">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-success/15" : pending ? "bg-warning/15" : "bg-surface-hover border border-border"}`}>
            {done ? <CheckCircle2 size={16} className="text-success-c" /> :
             pending ? <Clock size={14} className="text-warning-c" /> :
             locked ? <Lock size={14} className="text-faint-c" /> :
             <span className="text-xs text-secondary-c">·</span>}
        </div>
        <div className="flex-1">
            <div className={`text-sm ${done ? "text-text-primary" : pending ? "text-text-primary" : "text-secondary-c"}`}>{label}</div>
            {sub}
        </div>
    </li>
);

export default Dashboard;
